import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import {
  AlertIdData,
  DeliveryExceptionsReasons,
  DeliveryExceptionsReasonsPlural,
  Notification,
} from '@usf/alert-types';
import { PanAppState, PlatformService } from '@panamax/app-state';
import { OrderService } from '../../../../../order/services/order.service';
import {
  firstValueFrom,
  filter,
  map,
  first,
  pairwise,
  tap,
  catchError,
} from 'rxjs';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NotificationMetadata } from '../../notification-details.page';
import { LoginService } from '@app/login/services';
import { CustomerStoreService } from '../../../../../ngrx-customer/services/customer/customer-store.service';
import { PATHS } from '@shared/constants/paths';

interface DeliveryException {
  reasonCode: string;
  reasonDescription: string;
  productName: string;
  productNumber: string;
  undeliveredQuantity: string;
  unitOfMeasure: string;
}

interface DeliveryExceptionGroup {
  reasonCode: string;
  reasonDescription: string;
  products: DeliveryException[];
}

interface DeliveryIssueViewModel {
  notification: Notification;
  orderNumber: string;
  deliveryExceptions: DeliveryException[];
  deliveryExceptionGroups: DeliveryExceptionGroup[];
}

@Component({
  selector: 'app-delivery-issue',
  templateUrl: './delivery-issue.component.html',
  styleUrls: ['./delivery-issue.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeliveryIssueComponent {
  private _notification: Notification;
  @Input() notificationMetadata: NotificationMetadata;

  @Input()
  public get notification(): Notification {
    return this._notification;
  }

  public set notification(value: Notification) {
    this._notification = value;
    this.buildViewModel();
  }

  public viewModel: DeliveryIssueViewModel;

  constructor(
    public readonly platformService: PlatformService,
    private readonly orderService: OrderService,
    private readonly router: Router,
    private readonly translateService: TranslateService,
    private readonly panAppState: PanAppState,
    private readonly loginService: LoginService,
    private readonly customerStoreService: CustomerStoreService,
  ) {}

  private buildViewModel(): void {
    if (!this._notification) {
      return;
    }

    const orderNumber = this._notification.alertContext?.find(
      x => x.key === 'orderNumber',
    )?.value;

    const exceptions: DeliveryException[] =
      this._notification.alertContext?.find(
        x => x.key === 'deliveryExceptions',
      )?.value;

    // Group exceptions by reasonCode
    const groupsMap: { [reasonCode: string]: DeliveryException[] } = {};
    exceptions?.forEach(ex => {
      if (!groupsMap[ex.reasonCode]) {
        groupsMap[ex.reasonCode] = [];
      }
      groupsMap[ex.reasonCode].push(ex);
    });

    // Build the groups array
    const deliveryExceptionGroups: DeliveryExceptionGroup[] = Object.keys(
      groupsMap,
    ).map(reasonCode => {
      const products = groupsMap[reasonCode];
      return {
        reasonCode,
        reasonDescription: this.translateService.instant(
          products.length > 1
            ? DeliveryExceptionsReasonsPlural[reasonCode]
            : DeliveryExceptionsReasons[reasonCode],
        ),
        products,
      };
    });

    // Optional: sort by arrayOrder if needed
    const arrayOrder = ['04', '01', '03', '08'];
    deliveryExceptionGroups.sort(
      (a, b) =>
        arrayOrder.indexOf(a.reasonCode) - arrayOrder.indexOf(b.reasonCode),
    );

    this.viewModel = {
      notification: this._notification,
      orderNumber: orderNumber || '',
      deliveryExceptions: exceptions || [],
      deliveryExceptionGroups,
    };
  }

  async navigateTo(notification: Notification) {
    // Check if customer switching is needed before navigation
    const currentCustomer = await firstValueFrom(
      this.panAppState.customer$.pipe(
        filter(customer => !!customer),
        map(customer => customer?.customerNumber ?? undefined),
        first(),
      ),
    );

    // Switch customer if needed
    if (
      currentCustomer &&
      String(currentCustomer) !== String(notification.customerNumber)
    ) {
      try {
        await this.doCustomerSwitch(notification);

        // Wait for customer switch to complete
        await firstValueFrom(
          this.customerStoreService.customerSwitchStatus$().pipe(
            pairwise(),
            tap(([_previous, current]) => {
              if (current === 'failed') {
                throw new Error('Customer switch failed');
              }
            }),
            filter(
              ([previous, current]) =>
                previous === 'pending' && current === 'complete',
            ),
            map(() => true),
            catchError(err => {
              console.error(err);
              throw err;
            }),
          ),
        );
      } catch (error) {
        console.error('Customer switch failed:', error);
        // If customer switch fails, go directly to my-orders page
        const params = AlertIdData.find(
          a => a.alertId === notification?.alertId,
        )?.url?.split('?')[1];
        this.router.navigateByUrl(PATHS.MY_ORDERS + '?' + params);
        return;
      }
    }

    // Proceed with navigation after successful customer switch (or no switch needed)
    const orderNumber = notification?.alertContext?.find(
      n => n.key === 'orderNumber',
    );
    const params = AlertIdData.find(
      a => a.alertId === notification?.alertId,
    )?.url?.split('?')[1];

    try {
      const order = await firstValueFrom(
        this.orderService?.selectOrderWithTandemNumber(
          Number(orderNumber?.value),
        ),
      );

      if (order) {
        const path =
          `${
            this.platformService.isTouch.value
              ? '/touch/order/submitted-order/'
              : '/desktop/order/submitted-order/'
          }` +
          `${order.orderHeader.customerNumber}:` +
          `${order.orderHeader.departmentNumber}:` +
          `${order.orderHeader.orderId}` +
          `?${params}`;
        this.router.navigateByUrl(path);
      } else {
        this.router.navigateByUrl(PATHS.MY_ORDERS + '?' + params);
      }
    } catch (error) {
      console.error('Navigation failed:', error);
      this.router.navigateByUrl(PATHS.MY_ORDERS + '?' + params);
    }
  }

  private async doCustomerSwitch(notification: Notification): Promise<void> {
    await this.loginService.switchToCustomer(
      notification?.customerNumber,
      notification?.departmentNumber ?? 0,
      notification?.divisionNumber,
    );
  }
}
